export type IncomingShareImageItem = {
  kind: 'image';
  uri: string;
  name: string;
  mimeType: string;
};

export type IncomingSharePdfItem = {
  kind: 'pdf';
  uri: string;
  name: string;
  mimeType: string;
};

export type IncomingShareTextItem = {
  kind: 'text';
  text: string;
};

export type IncomingSharePreviewItem = IncomingShareImageItem | IncomingSharePdfItem | IncomingShareTextItem;

export type IncomingSharePreview = {
  id: string;
  items: IncomingSharePreviewItem[];
};

type ShareItem = {
  kind?: string;
  uri?: string;
  name?: string;
  mimeType?: string;
  text?: string;
};

type IncomingShareLike = {
  id?: string;
  items?: ShareItem[];
};

type Listener = (preview: IncomingSharePreview | null) => void;

const REMOTE_SRC = /^(https?:|data:|blob:)/i;

const queue: IncomingSharePreview[] = [];
const listeners = new Set<Listener>();

const current = () => queue[0] || null;

const emit = () => {
  const preview = current();
  listeners.forEach(listener => listener(preview));
};

const fileName = (name: string | undefined, fallback: string) => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || fallback;
};

const toPreviewItem = (item: ShareItem): IncomingSharePreviewItem | null => {
  if (!item || typeof item !== 'object') return null;
  if (item.kind === 'text') {
    const text = typeof item.text === 'string' ? item.text : '';
    return text.trim() ? { kind: 'text', text } : null;
  }
  if ((item.kind === 'image' || item.kind === 'pdf') && typeof item.uri === 'string' && item.uri) {
    return {
      kind: item.kind,
      uri: item.uri,
      name: fileName(item.name, item.kind === 'pdf' ? 'document.pdf' : 'image'),
      mimeType: typeof item.mimeType === 'string' ? item.mimeType : ''
    };
  }
  return null;
};

export const incomingShareImageSrc = (uri: string, convertFileSrc: (fileUri: string) => string) => {
  if (typeof uri !== 'string' || !uri) {
    return '';
  }
  if (REMOTE_SRC.test(uri)) {
    return uri;
  }
  return convertFileSrc(uri);
};

/** Show this share now. Any shortcut already waiting is dropped. */
export const presentIncomingShare = (share: IncomingShareLike | null | undefined) => {
  if (!share || typeof share.id !== 'string' || !share.id.trim()) {
    return [] as IncomingSharePreview[];
  }
  if (queue.some(preview => preview.id === share.id)) {
    return [] as IncomingSharePreview[];
  }

  const items = (Array.isArray(share.items) ? share.items : [])
    .map(toPreviewItem)
    .filter((item): item is IncomingSharePreviewItem => !!item);
  if (!items.length) {
    return [] as IncomingSharePreview[];
  }

  const dropped = queue.slice();
  queue.splice(0, queue.length, { id: share.id, items });
  emit();
  return dropped;
};

export const presentIncomingShareImages = presentIncomingShare;

export const dismissIncomingShareImages = () => {
  queue.shift();
  emit();
};

export const subscribeIncomingShareImages = (listener: Listener) => {
  listeners.add(listener);
  listener(current());
  return () => {
    listeners.delete(listener);
  };
};

export const resetIncomingShareImagesForTests = () => {
  queue.splice(0, queue.length);
  listeners.clear();
};
